import { FC, useState } from 'react';
import { X } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import CustomButton from '../../shared/CustomButton/CustomButton';
import {
  microcycleTemplatesEndpoints,
  IMicrocycleTemplate,
  IMicrocycleDay,
  MicrocycleScope,
  MicrocycleVisibility,
} from '../../endpoints/microcycleTemplates';
import styles from './MicrocycleTemplates.module.scss';

/**
 * Modal editor template microciclo.
 * Supporta create + update. Struttura giornaliera con target CE/IPS/CLS.
 */
const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const emptyDay = (dayIndex: number): IMicrocycleDay => ({
  dayIndex,
  mdPosition: '',
  sessionType: '',
  targetCE: { min: 0, max: 0 },
  targetIPS: { min: 0, max: 0 },
  targetCLS: { min: 0, max: 0 },
});

export const TemplateEditorModal: FC<{
  template: IMicrocycleTemplate | null;
  sport: string;
  teamId?: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ template, sport, onClose, onSaved }) => {
  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [category, setCategory] = useState<'pro' | 'amateur' | 'youth'>(
    template?.category ?? 'amateur',
  );
  const [mdPattern, setMdPattern] = useState(template?.mdPattern ?? 'md_sunday');
  const [scope, setScope] = useState<MicrocycleScope>(
    template?.scope ?? 'PERSONAL',
  );
  const [visibility, setVisibility] = useState<MicrocycleVisibility>(
    template?.visibility ?? 'PRIVATE',
  );
  const [structure, setStructure] = useState<IMicrocycleDay[]>(
    template?.structure && template.structure.length === 7
      ? template.structure
      : Array.from({ length: 7 }, (_, i) => emptyDay(i)),
  );

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {
        name,
        description,
        sport,
        category,
        mdPattern,
        scope,
        visibility,
        structure,
      };
      if (template) {
        const res = await microcycleTemplatesEndpoints.update(template._id, body);
        return res.data;
      }
      const res = await microcycleTemplatesEndpoints.create(body);
      return res.data;
    },
    onSuccess: onSaved,
  });

  const updateDay = (idx: number, patch: Partial<IMicrocycleDay>) => {
    setStructure((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
    );
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{template ? 'Modifica template' : 'Nuovo template microciclo'}</h2>
          <button type="button" className={styles.modalClose} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>Nome</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Es. Microciclo rugby MD-domenica"
              />
            </div>
          </div>

          <div className={styles.field}>
            <label>Descrizione</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>Categoria</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as any)}
              >
                <option value="pro">Pro</option>
                <option value="amateur">Amateur</option>
                <option value="youth">Youth</option>
              </select>
            </div>
            <div className={styles.field}>
              <label>Pattern MD</label>
              <select
                value={mdPattern}
                onChange={(e) => setMdPattern(e.target.value)}
              >
                <option value="md_sunday">Domenica (MD-6→MD)</option>
                <option value="md_saturday">Sabato (MD-5→MD)</option>
                <option value="double_match">Doppia partita</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div className={styles.field}>
              <label>Scope</label>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as any)}
              >
                <option value="PERSONAL">Personale</option>
                <option value="TEAM">Team</option>
                <option value="SYSTEM">LEOPART (admin)</option>
              </select>
            </div>
            <div className={styles.field}>
              <label>Visibilità</label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as any)}
              >
                <option value="PRIVATE">Privata</option>
                <option value="CLUB">Club</option>
                <option value="COMMUNITY">Community</option>
              </select>
            </div>
          </div>

          <h4 className={styles.dayGridTitle}>Struttura settimanale</h4>
          <div className={styles.dayGrid}>
            {structure.map((day, i) => (
              <DayEditor
                key={i}
                label={DAY_LABELS[i]}
                day={day}
                onChange={(patch) => updateDay(i, patch)}
              />
            ))}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <CustomButton color="outline" size="sm" onClick={onClose}>
            Annulla
          </CustomButton>
          <CustomButton
            color="secondary"
            size="sm"
            disabled={!name.trim()}
            isLoading={saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {template ? 'Aggiorna' : 'Crea'}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

const DayEditor: FC<{
  label: string;
  day: IMicrocycleDay;
  onChange: (patch: Partial<IMicrocycleDay>) => void;
}> = ({ label, day, onChange }) => (
  <div className={styles.dayCard}>
    <div className={styles.dayCardHeader}>{label}</div>
    <div className={styles.dayField}>
      <label>MD</label>
      <input
        value={day.mdPosition ?? ''}
        onChange={(e) => onChange({ mdPosition: e.target.value })}
        placeholder="MD-3"
      />
    </div>
    <div className={styles.dayField}>
      <label>Tipo</label>
      <select
        value={day.sessionType ?? ''}
        onChange={(e) => onChange({ sessionType: e.target.value })}
      >
        <option value="">—</option>
        <option value="tactical">Tattico</option>
        <option value="physical">Fisico</option>
        <option value="technical">Tecnico</option>
        <option value="recovery">Recovery</option>
        <option value="match_prep">Match prep</option>
        <option value="match">Match</option>
        <option value="mixed">Misto</option>
      </select>
    </div>
    <div className={styles.dayTarget}>
      <span className={styles.dayTargetLabel}>CE</span>
      <input
        type="number"
        value={day.targetCE?.min ?? 0}
        onChange={(e) =>
          onChange({
            targetCE: {
              min: Number(e.target.value),
              max: day.targetCE?.max ?? 0,
            },
          })
        }
      />
      <span>–</span>
      <input
        type="number"
        value={day.targetCE?.max ?? 0}
        onChange={(e) =>
          onChange({
            targetCE: {
              min: day.targetCE?.min ?? 0,
              max: Number(e.target.value),
            },
          })
        }
      />
    </div>
    <div className={styles.dayTarget}>
      <span className={styles.dayTargetLabel}>IPS</span>
      <input
        type="number"
        step="0.1"
        value={day.targetIPS?.min ?? 0}
        onChange={(e) =>
          onChange({
            targetIPS: {
              min: Number(e.target.value),
              max: day.targetIPS?.max ?? 0,
            },
          })
        }
      />
      <span>–</span>
      <input
        type="number"
        step="0.1"
        value={day.targetIPS?.max ?? 0}
        onChange={(e) =>
          onChange({
            targetIPS: {
              min: day.targetIPS?.min ?? 0,
              max: Number(e.target.value),
            },
          })
        }
      />
    </div>
    <div className={styles.dayTarget}>
      <span className={styles.dayTargetLabel}>CLS</span>
      <input
        type="number"
        step="0.1"
        value={day.targetCLS?.min ?? 0}
        onChange={(e) =>
          onChange({
            targetCLS: {
              min: Number(e.target.value),
              max: day.targetCLS?.max ?? 0,
            },
          })
        }
      />
      <span>–</span>
      <input
        type="number"
        step="0.1"
        value={day.targetCLS?.max ?? 0}
        onChange={(e) =>
          onChange({
            targetCLS: {
              min: day.targetCLS?.min ?? 0,
              max: Number(e.target.value),
            },
          })
        }
      />
    </div>
  </div>
);
